import React, { useState, useEffect, useContext } from 'react';
import { Box, makeStyles, TextField } from '@material-ui/core';
import { HomeComponentContainer } from './styled';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { colorSet, useSnackbar } from '@components/Provider';
import {
  CapaErrorCode,
  useSubscribeCapaNewsMutation
} from '@generated/graphql';
import { MediaQuery } from '@routes/Home/HomePresenter';
import useUserActionLog from '@hooks/useUserActionLog';

const NewsLetterSubscription: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string>('');
  const [emailValidate, setEmailValidate] = useState<boolean>(false);
  const [nameValidate, setNameValidate] = useState<boolean>(false);
  const [buttonState, setButtonState] = useState<boolean>(true);
  const { container, textBox, formBox, homeContainer } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [subscribeCAPANews] = useSubscribeCapaNewsMutation();
  const { md, sm } = useContext(MediaQuery);
  const { insertButtonLog } = useUserActionLog();

  useEffect(() => {
    if (organizationName.trim().length <= 0) {
      setNameValidate(false);
    } else {
      setNameValidate(true);
    }
    if (
      email.match(
        /[-A-Za-z0-9_]+[-A-Za-z0-9_.]*[@]{1}[-A-Za-z0-9_]+[-A-Za-z0-9_.]*[.]{1}[A-Za-z]{1,5}/
      ) === null
    ) {
      setEmailValidate(false);
    } else {
      setEmailValidate(true);
    }
  }, [email, organizationName]);

  const handleSubmit = () => {
    insertButtonLog('Button_Landing_Subscription_button');
    setButtonState(false);
    if (emailValidate && nameValidate) {
      subscribeCAPANews({
        variables: { subscriberEmail: email, userName: organizationName }
      }).then(
        (res) => {
          if (res.data?.subscribeCAPANews.result === CapaErrorCode.Success) {
            enqueueSnackbar(`CAPA를 구독해 주셔서 감사합니다.`, {
              variant: 'success'
            });
            setButtonState(true);
          } else if (
            res.data?.subscribeCAPANews.result ===
            CapaErrorCode.AlreadySubscribed
          ) {
            enqueueSnackbar(`이미 캐파를 구독하고 있습니다.`, {
              variant: 'error'
            });
            setButtonState(true);
          } else {
            enqueueSnackbar('구독에 실패했습니다. 잠시후 다시 시도해주세요.', {
              variant: 'error'
            });
            setButtonState(true);
          }
        },
        (error) => {
          enqueueSnackbar('네트워크가 불안정합니다. 다시 시도해주세요.', {
            variant: 'error'
          });
          setButtonState(true);
        }
      );
    } else if (!emailValidate) {
      if (email.trim().length <= 0) {
        enqueueSnackbar('이메일을 입력해주세요.', {
          variant: 'error'
        });
        setButtonState(true);
      } else {
        enqueueSnackbar('이메일을 확인해주세요.', {
          variant: 'error'
        });
        setButtonState(true);
      }
    }
    if (!nameValidate) {
      enqueueSnackbar('이름을 입력해주세요.', {
        variant: 'error'
      });
      setButtonState(true);
    }
  };

  return (
    <HomeComponentContainer
      className={homeContainer}
      padding={sm ? '100px 16px' : md ? '120px 36px' : '120px 0'}
    >
      <Box className={container}>
        <Box className={textBox}>
          <CustomizedTypography
            variant={!sm ? 'h1' : undefined}
            fontWeight="700"
            gutterBottoms={sm ? 32 : 0}
          >
            <b>'캐파 뉴스레터'</b>를 구독하고 <br />
            캐파 서비스와 제조업과 관련된 <br />
            최신소식을 받아보세요.
          </CustomizedTypography>
        </Box>
        <Box className={formBox}>
          <TextField
            variant="filled"
            size="medium"
            placeholder="이름을 입력해주세요."
            type="text"
            onChange={(e) => {
              setOrganizationName(e.currentTarget.value);
            }}
            margin="none"
            onFocus={() => {
              insertButtonLog('Button_Landing_Subscription_input');
            }}
            InputProps={{
              margin: 'none'
            }}
          />
          <TextField
            variant="filled"
            size="medium"
            placeholder="이메일을 입력하세요"
            type="email"
            onChange={(e) => {
              setEmail(e.currentTarget.value);
            }}
            margin="none"
            onFocus={() => {
              insertButtonLog('Button_Landing_Subscription_input');
            }}
            InputProps={{
              margin: 'none'
            }}
          />
          <CustomizedButton
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={!buttonState}
          >
            구독하기
          </CustomizedButton>
        </Box>
      </Box>
    </HomeComponentContainer>
  );
};

export default NewsLetterSubscription;

const useStyles = makeStyles((theme) => ({
  homeContainer: {
    backgroundColor: colorSet.gray100
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    '& b': {
      color: colorSet.primary500
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      justifyContent: 'normal'
    }
  },
  textBox: {
    minWidth: 505,
    [theme.breakpoints.down('sm')]: {
      '& p': {
        fontSize: '25px'
      }
    }
  },
  formBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    '& .MuiFilledInput-root': {
      borderRadius: '4px',
      backgroundColor: colorSet.gray200
    },
    '& .MuiFilledInput-input': {
      padding: '14px 18px'
    },
    '& .MuiFilledInput-underline': {
      '&:before': {
        display: 'none'
      },
      '&:after': {
        display: 'none'
      }
    },
    '& button': {
      marginTop: 24,
      backgroundColor: colorSet.gray900,
      [theme.breakpoints.down('sm')]: {
        marginTop: 16
      }
    },
    [theme.breakpoints.down('sm')]: {
      gap: '8px'
    },
    '& input': {
      minWidth: 340,
      minHeight: 20,
      [theme.breakpoints.down('md')]: {
        minWidth: 304
      }
    }
  }
}));
